import { type Ref, readonly, ref } from 'vue';

import { canUseSpotlightSearch } from 'src/util/platform/browserInteractionCapabilities';

export interface SpotlightCommand {
	execute: () => Promise<void> | void;
	icon: string;
	id: string;
	keywords: string[];
	label: string;
	section: 'action' | 'create' | 'navigation' | 'settings';
	shortcut?: string;
	visible?: () => boolean;
}

interface SpotlightSearchState {
	close: () => void;
	commands: Readonly<Ref<ReadonlyArray<SpotlightCommand>>>;
	isOpen: Readonly<Ref<boolean>>;
	open: (defaultModule?: null | string) => void;
	registerCommands: (cmds: SpotlightCommand[]) => void;
	scopedModule: Readonly<Ref<null | string>>;
	searchQuery: Ref<string>;
	selectedIndex: Ref<number>;
	setScopedModule: (mod: null | string) => void;
	toggle: (defaultModule?: null | string) => void;
}

const isOpenRef = ref(false);
const searchQueryRef = ref('');
const selectedIndexRef = ref(0);
const commandsRef = ref<SpotlightCommand[]>([]);
const scopedModuleRef = ref<null | string>(null);

function open(defaultModule: null | string = null): void {
	if (!canUseSpotlightSearch(true)) {
		return;
	}

	searchQueryRef.value = '';
	selectedIndexRef.value = 0;
	scopedModuleRef.value = defaultModule;
	isOpenRef.value = true;
}

function close(): void {
	isOpenRef.value = false;
}

function toggle(defaultModule: null | string = null): void {
	if (isOpenRef.value) {
		close();
	} else {
		open(defaultModule);
	}
}

function registerCommands(cmds: SpotlightCommand[]): void {
	commandsRef.value = cmds;
}

function setScopedModule(mod: null | string): void {
	scopedModuleRef.value = mod;
}

export function useSpotlightSearch(): SpotlightSearchState {
	return {
		close,
		commands: readonly(commandsRef) as Readonly<Ref<ReadonlyArray<SpotlightCommand>>>,
		isOpen: readonly(isOpenRef),
		open,
		registerCommands,
		scopedModule: readonly(scopedModuleRef),
		searchQuery: searchQueryRef,
		selectedIndex: selectedIndexRef,
		setScopedModule,
		toggle,
	};
}
